import { useState, useCallback } from 'react';
import axios from 'axios';

export interface ValidationError {
  sampleId: number | string | null;
  field: string;
  errorCode: string;
  message: string;
  version: string | number | null;
}

interface FileUploadError {
  message: string;
  details?: string[];
  code?: string;
  validationErrors?: ValidationError[];
}

interface FileUploadHook {
  selectedFile: File | null;
  isUploading: boolean;
  uploadProgress: number;
  uploadError: FileUploadError | null;
  validationErrors: ValidationError[];
  handleFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleUpload: () => Promise<any>;
  uploadToEndpoint: (endpoint: string) => Promise<any>;
  uploadFileToEndpoint: (file: File, endpoint: string) => Promise<any>;
  resetUpload: () => void;
}

interface FileUploadOptions {
  onUploadSuccess?: (response: any) => void;
  onUploadError?: (error: FileUploadError, validationErrors?: ValidationError[]) => void;
  allowedFileTypes?: string[];
  maxFileSizeMB?: number;
  uploadEndpoint?: string;
  keepFileAfterUpload?: boolean;
}

/**
 * Extract validation errors from various response structures
 * @param errorResponse The error response object
 * @returns Array of validation errors
 */
function extractValidationErrors(errorResponse: any): ValidationError[] {
  if (!errorResponse) return [];

  // Check common error response patterns
  if (errorResponse.errors && Array.isArray(errorResponse.errors)) {
    // Case 1: { errors: [...] }
    return errorResponse.errors;
  } else if (errorResponse.errorResponse && errorResponse.errorResponse.errors && 
             Array.isArray(errorResponse.errorResponse.errors)) {
    // Case 2: { errorResponse: { errors: [...] } }
    return errorResponse.errorResponse.errors;
  } else if (Array.isArray(errorResponse)) {
    // Case 3: Direct array of errors
    return errorResponse;
  }

  console.warn('Unknown error response structure:', errorResponse);
  return [];
}

/**
 * Hook to handle file uploads with progress tracking
 * @param options Configuration options for the file upload
 * @returns Object with file upload state and handler functions
 */
export function useFileUpload(options: FileUploadOptions = {}): FileUploadHook {
  const {
    onUploadSuccess,
    onUploadError,
    allowedFileTypes = ['.csv'],
    maxFileSizeMB = 5,
    uploadEndpoint = '/data-set/eno1',
    keepFileAfterUpload = true // Default to keeping files after upload for multi-step processes
  } = options;

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [uploadError, setUploadError] = useState<FileUploadError | null>(null);
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);

  const handleFileChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];
      
      // Check file size
      if (file.size > maxFileSizeMB * 1024 * 1024) {
        setUploadError({
          message: `File size exceeds the maximum allowed size of ${maxFileSizeMB}MB`,
          code: 'FILE_TOO_LARGE'
        });
        return;
      }
      
      // Check file type if allowedFileTypes is provided
      if (allowedFileTypes.length > 0) {
        const fileExtension = file.name.split('.').pop()?.toLowerCase();
        const isValidType = allowedFileTypes.some(type => 
          type.toLowerCase().includes(fileExtension || '')
        );
        
        if (!isValidType) {
          setUploadError({
            message: `File type not allowed. Accepted types: ${allowedFileTypes.join(', ')}`,
            code: 'INVALID_FILE_TYPE'
          });
          return;
        }
      }
      
      setSelectedFile(file);
      setUploadError(null);
      setValidationErrors([]);
    }
  }, [allowedFileTypes, maxFileSizeMB]);

  // Core upload function that handles all the upload logic
  const uploadFile = useCallback(async (endpoint: string, customSuccess?: (response: any) => void, fileOverride?: File) => {
    const fileToUpload = fileOverride ?? selectedFile;

    if (!fileToUpload) {
      setUploadError({
        message: 'No file selected',
        code: 'NO_FILE_SELECTED'
      });
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);
    setUploadError(null);
    setValidationErrors([]);

    // Create form data
    const formData = new FormData();
    formData.append('dataSet', fileToUpload);

    try {
      // Use axios for the upload with progress tracking
      const response = await axios.post(endpoint, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(progress);
          }
        },
      });

      // Handle successful response
      if (customSuccess) {
        customSuccess(response.data);
      } else if (onUploadSuccess) {
        onUploadSuccess(response.data);
      }
      
      // First complete the progress
      setUploadProgress(100);
      
      // Only reset the selectedFile after a short delay if keepFileAfterUpload is false
      if (!keepFileAfterUpload) {
        setTimeout(() => {
          setSelectedFile(null);
        }, 300);
      }
      
      return response.data;
      
    } catch (error) {
      console.error('Error uploading file:', error);
      
      // Extract error details from axios error
      const axiosError = error as any;
      const errorResponse = axiosError?.response?.data;
      
      // Extract validation errors from the response using our helper function
      const validationErrors = extractValidationErrors(errorResponse);
      
      if (validationErrors.length > 0) {
        setValidationErrors(validationErrors);
      }
      
      const uploadError: FileUploadError = {
        message: errorResponse?.message || 'Failed to upload file. Please try again.',
        details: errorResponse?.details || [],
        code: errorResponse?.code || 'UPLOAD_FAILED',
        validationErrors: validationErrors
      };
      
      setUploadError(uploadError);
      
      // Call error callback if provided
      if (onUploadError) {
        onUploadError(uploadError, validationErrors);
      }
      
      // Throw enriched error object
      throw {
        originalError: axiosError,
        uploadError,
        validationErrors
      };
    } finally {
      setIsUploading(false);
    }
  }, [selectedFile, onUploadSuccess, onUploadError, keepFileAfterUpload]);

  // Handle upload using the default endpoint
  const handleUpload = useCallback(() => {
    return uploadFile(uploadEndpoint);
  }, [uploadFile, uploadEndpoint]);

  // Handle upload to a specific endpoint
  const uploadToEndpoint = useCallback((endpoint: string) => {
    return uploadFile(endpoint);
  }, [uploadFile]);

  // Handle upload of a provided File to a specific endpoint (bypasses selectedFile state)
  const uploadFileToEndpoint = useCallback((file: File, endpoint: string) => {
    return uploadFile(endpoint, undefined, file);
  }, [uploadFile]);

  const resetUpload = useCallback(() => {
    setSelectedFile(null);
    setIsUploading(false);
    setUploadProgress(0);
    setUploadError(null);
    setValidationErrors([]);
  }, []);

  return {
    selectedFile,
    isUploading,
    uploadProgress,
    uploadError,
    validationErrors,
    handleFileChange,
    handleUpload,
    uploadToEndpoint,
    uploadFileToEndpoint,
    resetUpload
  };
} 